"use client";

import { useRef, useEffect } from "react";
import { FilterConfig, SelectedMap } from "./Types";
import Image from "next/image";

// ─── Icons ────────────────────────────────────────────────────────────────────
const ChevronDown: React.FC<{ size?: number; className?: string }> = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.17"
    className="text-[#4A5565]"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CheckIcon: React.FC<{ size?: number }> = ({ size = 11 }) => (
  <Image
      src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}check.svg`}
      alt="check"
      title="check"
      width={size}
      height={size}
  />
);

// ─── Props ────────────────────────────────────────────────────────────────────
interface FilterDropdownProps {
  filter: FilterConfig;
  openFilter: string | null;
  setOpenFilter: (val: string | null) => void;
  selectedMap: SelectedMap;
  onSelect: (
    filterKey: string,
    value: string,
    type: "single" | "multi",
  ) => void;
  applyFilterTrackingClass?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function FilterDropdown({
  filter,
  openFilter,
  setOpenFilter,
  selectedMap,
  onSelect,
  applyFilterTrackingClass = '',
}: FilterDropdownProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isOpen = openFilter === filter.key;
  const selected: string[] = selectedMap[filter.key] ?? [];

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        if (openFilter === filter.key) setOpenFilter(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openFilter, filter.key, setOpenFilter]);

  const handleToggle = () => setOpenFilter(isOpen ? null : filter.key);

  const handleSelect = (value: string) => {
    onSelect(filter.key, value, filter.type);
    if (filter.type === "single") setOpenFilter(null);
  };

  if(filter.options.length === 0) return null;

  return (
    <div ref={ref} className="relative">
      {/* Pill button */}
      <button
        className={`flex items-center gap-[7px] py-1.5 px-3 border-[1.5px] rounded-[999px] bg-white text-[12px] leading-5 font-medium cursor-pointer whitespace-nowrap transition-[border-color,color] duration-150 ease-[ease] [.fillterTwoSection_&]:gap-[5px] [.fillterTwoSection_&]:px-2 ${isOpen || selected.length > 0 ? "border-[#008C8B] text-[#008C8B]" : "border-[#E5E7EB] text-[#4A5565]"}`}
        onClick={handleToggle}
        type="button"
      >
        {filter.title}

        {/* {selected.length > 0 && (
          <span className="bg-[#008C8B] text-white rounded-full w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold shrink-0">{selected.length}</span>
        )} */}

        <ChevronDown />
      </button>

      {/* Dropdown panel */}
      <div
        className={`absolute top-[calc(100%+18px)] left-0 bg-white border-[1.5px] border-[#e5e7eb] rounded-xl min-w-[200px] shadow-[0_8px_24px_rgba(0,0,0,0.1)] z-[100] py-1.5 max-h-[238px] overflow-auto [scrollbar-color:#eee_#fff] [scrollbar-width:thin] ${isOpen ? "block" : "hidden"}`}
      >
        <div className="pt-2 px-3.5 pb-1.5 text-[10px] font-bold tracking-[0.08em] text-[#9ca3af] uppercase">Select Type</div>

        {filter.options.map((option) => {
          const isSelected = selected.includes(option.value);

          return (
            <div
              key={option.value}
              className={`flex items-center gap-2.5 py-[9px] px-3.5 cursor-pointer text-[13.5px] text-[#1f2937] transition-[background] duration-120 ease-[ease] ${isSelected ? "bg-[#f0fdfb] font-semibold hover:bg-[#f0fdfb]" : "hover:bg-[#f9fafb]"} ${applyFilterTrackingClass}`}
              onClick={() => handleSelect(option.value)}
            >
              {/* Checkbox / Radio indicator */}
              <span
                className={`w-[18px] h-[18px] border-2 flex items-center justify-center shrink-0 transition-[border-color,background] duration-120 ease-[ease] ${
                  filter.type === "multi"
                    ? "rounded-sm"
                    : "rounded-full"
                } ${isSelected ? "text-white bg-[#008C8B] border-[#008C8B]" : "bg-white border-[#d1d5db]"}`}
              >
                {isSelected && <CheckIcon />}
              </span>

              {option.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}
